import { useState, useMemo } from "react";
import { Ruler, Check, X, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { areDimensionsPlausible, extractItemDims } from "@/lib/dimensions";

interface SizeGuideProps {
  description: string;
  title: string;
}

// Item dimensions only — never the shipping carton's (see lib/dimensions).
function extractDims(desc: string): { width?: number; depth?: number; height?: number } {
  const d = extractItemDims(desc);
  const n = (v?: string) => (v ? parseFloat(v) : undefined);
  return { width: n(d?.Width ?? d?.Length), depth: n(d?.Depth), height: n(d?.Height) };
}

export const SizeGuide = ({ description, title }: SizeGuideProps) => {
  const productDims = useMemo(() => extractDims(description), [description]);
  const [roomWidth, setRoomWidth] = useState("");
  const [roomDepth, setRoomDepth] = useState("");
  const [roomHeight, setRoomHeight] = useState("");
  const [checked, setChecked] = useState(false);

  // Don't run a fit calculator on implausible (likely transposed) feed dimensions.
  const dimsPlausible = useMemo(
    () =>
      areDimensionsPlausible(
        {
          ...(productDims.width ? { Width: `${productDims.width}"` } : {}),
          ...(productDims.depth ? { Depth: `${productDims.depth}"` } : {}),
          ...(productDims.height ? { Height: `${productDims.height}"` } : {}),
        },
        title
      ),
    [productDims, title]
  );

  const hasDims =
    dimsPlausible && (productDims.width || productDims.depth || productDims.height);

  const results = useMemo(() => {
    if (!checked) return null;
    const rw = parseFloat(roomWidth);
    const rd = parseFloat(roomDepth);
    const rh = parseFloat(roomHeight);
    const issues: string[] = [];
    let fits = true;
    let comparisons = 0;

    if (productDims.width && rw) {
      comparisons++;
      if (productDims.width > rw) {
        fits = false;
        issues.push(`Product width (${productDims.width}") exceeds room width (${rw}")`);
      } else if (productDims.width > rw * 0.8) {
        issues.push(`Product takes up ${Math.round((productDims.width / rw) * 100)}% of room width — tight fit`);
      }
    }
    if (productDims.depth && rd) {
      comparisons++;
      if (productDims.depth > rd) {
        fits = false;
        issues.push(`Product depth (${productDims.depth}") exceeds room depth (${rd}")`);
      } else if (productDims.depth > rd * 0.6) {
        issues.push(`Product takes up ${Math.round((productDims.depth / rd) * 100)}% of room depth`);
      }
    }
    if (productDims.height && rh) {
      comparisons++;
      if (productDims.height > rh) {
        fits = false;
        issues.push(`Product height (${productDims.height}") exceeds ceiling height (${rh}")`);
      }
    }

    if (comparisons === 0) {
      return { fits: true, issues: ["Enter your room dimensions above to compare against product measurements."], noData: true };
    }

    if (fits && issues.length === 0) {
      const details: string[] = [];
      if (productDims.width && rw) details.push(`Width: ${productDims.width}" fits in ${rw}" (${Math.round((productDims.width / rw) * 100)}% of room)`);
      if (productDims.depth && rd) details.push(`Depth: ${productDims.depth}" fits in ${rd}" (${Math.round((productDims.depth / rd) * 100)}% of room)`);
      if (productDims.height && rh) details.push(`Height: ${productDims.height}" under ${rh}" ceiling`);
      return { fits: true, issues: details, noData: false };
    }

    return { fits, issues, noData: false };
  }, [checked, roomWidth, roomDepth, roomHeight, productDims]);

  if (!hasDims) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border-t border-border"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Ruler className="h-5 w-5 text-foreground/40" />
              <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-medium">
                Size Calculator
              </p>
            </div>
            <h2 className="font-sans text-[22px] md:text-[26px] font-semibold text-foreground mb-2">
              Will It Fit?
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Enter your room dimensions to check if this piece will work in your space.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[12px] text-muted-foreground font-medium block mb-1">Room Width</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={roomWidth}
                      onChange={(e) => { setRoomWidth(e.target.value); setChecked(false); }}
                      placeholder="0"
                      className="w-full border border-border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">in</span>
                  </div>
                </div>
                <div>
                  <label className="text-[12px] text-muted-foreground font-medium block mb-1">Room Depth</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={roomDepth}
                      onChange={(e) => { setRoomDepth(e.target.value); setChecked(false); }}
                      placeholder="0"
                      className="w-full border border-border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">in</span>
                  </div>
                </div>
                <div>
                  <label className="text-[12px] text-muted-foreground font-medium block mb-1">Ceiling</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={roomHeight}
                      onChange={(e) => { setRoomHeight(e.target.value); setChecked(false); }}
                      placeholder="96"
                      className="w-full border border-border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">in</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setChecked(true)}
                disabled={!roomWidth && !roomDepth}
                className="w-full bg-foreground text-background py-2.5 rounded text-sm font-medium hover:bg-foreground/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Check Fit
              </button>
            </div>

            {results && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-5 p-4 rounded-lg border ${
                  results.noData
                    ? "border-border bg-secondary/30"
                    : results.fits
                    ? "border-primary/30 bg-primary/5"
                    : "border-destructive/30 bg-destructive/5"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {results.noData ? (
                    <>
                      <AlertTriangle className="h-5 w-5 text-muted-foreground" />
                      <span className="font-semibold text-sm text-muted-foreground">Need more info</span>
                    </>
                  ) : results.fits ? (
                    <>
                      <Check className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-sm text-foreground">Great fit!</span>
                    </>
                  ) : (
                    <>
                      <X className="h-5 w-5 text-destructive" />
                      <span className="font-semibold text-sm text-destructive">May not fit</span>
                    </>
                  )}
                </div>
                {results.issues.length > 0 && (
                  <ul className="space-y-1 mt-2">
                    {results.issues.map((issue, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-[12px] text-muted-foreground">
                        {results.noData ? null : results.fits ? (
                          <Check className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        )}
                        {issue}
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            )}
          </div>

          <div className="bg-secondary/30 rounded-lg p-6 md:p-8">
            <h3 className="text-sm font-semibold text-foreground mb-4">Product Dimensions</h3>
            <div className="space-y-3">
              {productDims.width && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Width</span>
                  <span className="font-medium">{productDims.width}"</span>
                </div>
              )}
              {productDims.depth && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Depth</span>
                  <span className="font-medium">{productDims.depth}"</span>
                </div>
              )}
              {productDims.height && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Height</span>
                  <span className="font-medium">{productDims.height}"</span>
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-4 border-t border-border pt-3">
              💡 Tip: Measure doorways and hallways too — the product needs to get through them!
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
